import { Component, OnDestroy, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthError, AuthenticationResult, EventMessage, EventType, IdTokenClaims, InteractionStatus, PromptValue, RedirectRequest } from '@azure/msal-browser';
import { Subject, firstValueFrom } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { b2cPolicies, loginRequest } from 'src/app/auth-config';
import { SidebarConstants } from 'src/app/layout/sidebar/Constants/sidebar.constants';
import { AppContextService } from '../../services/app-context.service';
import { NavigationService } from '../../services/navigation.service';
import { SecurityService } from '../../services/security.service';
import { UserLookupCacheService } from '../../services/user-lookup-cache.service';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
	acr?: string;
	tfp?: string;
};

const b2cPoliciesNames = {
	signUpSignIn: 'b2c_1a_signin',
	resetPassword: 'b2c_1a_passwordreset',
};

@Component({
	selector: 'app-auth',
	templateUrl: `./auth.component.html`,
	standalone: false,
})
export class AuthComponent implements OnInit, OnDestroy {
	private readonly _destroying$ = new Subject<void>();
	private readonly redirectGuardKey = 'auth:lastRedirectAt';
	private readonly activeAccountId = 'auth:active_id';
	private isInitialAuthComplete = false;

	constructor(
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private securityService: SecurityService,
		private navigationService: NavigationService,
		private appContext: AppContextService,
		private userLookupCache: UserLookupCacheService
	) {}

	ngOnInit() {
		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || (msg.error?.message?.indexOf('AADB2C90118') ?? -1) > -1),
				takeUntil(this._destroying$)
			)
			.subscribe((msg: EventMessage) => {
				this.handleAuthFailure(msg);
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
				takeUntil(this._destroying$)
			)
			.subscribe((msg: EventMessage) => {
				localStorage.removeItem('lastApiCall');
				const payload = msg.payload as AuthenticationResult;
				const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;
				if (idtoken.acr === b2cPoliciesNames.signUpSignIn || idtoken.acr === b2cPoliciesNames.resetPassword) {
					this.msalService.instance.setActiveAccount(payload.account);
					localStorage.setItem(this.activeAccountId, payload.account.homeAccountId);
				}
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
				takeUntil(this._destroying$)
			)
			.subscribe((msg: EventMessage) => {
				this.handleTokenAcquisitionFailure(msg);
			});

		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
				takeUntil(this._destroying$)
			)
			.subscribe((_msg: EventMessage) => {
				if (!this.isInitialAuthComplete) {
					this.isInitialAuthComplete = true;
					this.proceedWithAuthentication();
				}
			});

		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status: InteractionStatus) => status === InteractionStatus.None),
				takeUntil(this._destroying$)
			)
			.subscribe(event => {
				this.handleAuthenticationFlow();
			});
	}

	private handleAuthFailure(msg: EventMessage): void {
		let request: RedirectRequest = {
			scopes: loginRequest.scopes,
			authority: b2cPolicies.authorities.signIn.authority,
			prompt: PromptValue.LOGIN,
		};

		if (msg.error && msg.error.message) {
			if (msg.error.message.indexOf('AADB2C90118') > -1) {
				request = { ...request, authority: b2cPolicies.authorities.passwReset.authority };
			} else if (msg.error.message.indexOf('AADB2C90091') > -1) {
				request = { ...request, prompt: PromptValue.LOGIN };
			} else {
				request = {
					...request,
					extraQueryParameters: { error_message: msg.error.message },
				};
			}
		}

		this.scheduleLoginRedirect(request);
	}

	private handleTokenAcquisitionFailure(msg: EventMessage): void {
		const err = msg.error as AuthError | undefined;
		if (!err) return;

		if (
			err.errorCode === 'consent_required' ||
			err.errorCode === 'interaction_required' ||
			err.errorCode === 'login_required' ||
			err.message?.includes('monitor_window_timeout')
		) {
			this.logoutAndRedirectUserToLogin();
		}
	}

	private handleAuthenticationFlow(): void {
		const isAuthenticated = this.isAuthenticated;

		if (isAuthenticated) {
			this.acquireTokenSilently();
		} else {
			this.logoutAndRedirectUserToLogin();
		}
	}

	private acquireTokenSilently(): void {
		const accounts = this.msalService.instance.getAllAccounts();
		const currentAccountId = localStorage.getItem(this.activeAccountId);
		const account = accounts.find(acc => acc.homeAccountId === currentAccountId);

		if (account) {
			const silentRequest = {
				scopes: loginRequest.scopes,
				account: account,
				authority: b2cPolicies.authorities.signIn.authority,
			};

			this.msalService.acquireTokenSilent(silentRequest).subscribe({
				error: _error => {
					this.logoutAndRedirectUserToLogin();
				},
			});
		}
	}

	private scheduleLoginRedirect(request: RedirectRequest): void {
		const now = Date.now();
		const last = Number(sessionStorage.getItem(this.redirectGuardKey) || 0);

		if (now - last < 2000) {
			return;
		}

		sessionStorage.setItem(this.redirectGuardKey, String(now));

		this.msalBroadcastService.inProgress$
			.pipe(
				filter((s: InteractionStatus) => s === InteractionStatus.None),
				take(1),
				takeUntil(this._destroying$)
			)
			.subscribe(() => {
				this.msalService.loginRedirect(request);
			});
	}

	private proceedWithAuthentication(): void {
		this.securityService.loginCurrentUser().subscribe(x => {
			if (x) {
				if (this.appContext.isCustomerUser()) {
					this.navigationService.navigateToMenuLinks(SidebarConstants.MenuLinks.CustomerDashboard);
				} else {
					this.navigationService.navigateToMenuLinks(SidebarConstants.MenuLinks.DashBoard);
				}
			} else {
				this.navigationService.navigateToCurrentSubscriptions();
			}
		});
	}

	private waitForInteractionNone(): Promise<void> {
		return firstValueFrom(
			this.msalBroadcastService.inProgress$.pipe(
				filter((s: InteractionStatus) => s === InteractionStatus.None),
				take(1),
				takeUntil(this._destroying$)
			)
		)
			.then(() => undefined)
			.catch(() => undefined);
	}

	get isAuthenticated() {
		return this.msalService.instance.getAllAccounts().length > 0;
	}

	parseError(response: string) {
		const matches = response.match(/error_description=([^&]*)/);
		return matches && matches.length > 0 ? matches[1] : null;
	}

	logoutAndRedirectUserToSubscription() {
		this.securityService.logout(true);
	}

	logoutAndRedirectUserToLogin() {
		this.appContext.clearUser();
		this.userLookupCache.clearCache();
		localStorage.removeItem('lastApiCall');
		this.waitForInteractionNone().then(() => {
			this.msalService.logout();
		});
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}
}
